import {
  createContext,
  type ReactNode,
  type ReactElement,
  useContext,
  useReducer, useMemo,
} from 'react'
import type NavigationContextProps from './NavigationContext.types'

interface Action {
  readonly type: 'setNavigationProps'
  readonly payload: NavigationContextProps
}

type Dispatch = (action: Action) => void

interface State {
  readonly navigationContext: NavigationContextProps
}

interface NavigationContextProviderProps {
  readonly children: ReactNode
}

interface NavigationStateContextType {
  readonly navigationState: State
  readonly dispatch: Dispatch
}

const NavigationStateContext = createContext<
  NavigationStateContextType | undefined
>(undefined)

function navigationReducer(state: State, action: Action): State {
  if (action.type === 'setNavigationProps') {
    return { navigationContext: action.payload }
  } else {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    throw new Error(`Unhandled action type: ${action.type}`)
  }
}

function NavigationContextProvider({
  children,
}: NavigationContextProviderProps): ReactElement {
  const [navigationState, dispatch] = useReducer(navigationReducer, {
    navigationContext: {
      breadcrumbHome: { name: 'home', link: '/' },
      breadcrumbActive: '',
      activeLink: '',
      scrollPosition: 0,
    },
  })
  const value = useMemo(() => ({ navigationState, dispatch }), [])

  return (
    <NavigationStateContext.Provider value={value}>
      {children}
    </NavigationStateContext.Provider>
  )
}

function useNavigationContext(): NavigationStateContextType {
  const context = useContext(NavigationStateContext)
  if (context === undefined) {
    throw new Error('error')
  }
  return context
}

export { NavigationContextProvider, useNavigationContext }
